// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import { ReactElement, ReactNode } from 'react';
import { useCallback } from 'react';
import { useState } from 'react';
import { Comment, CommentForm } from '@Molecules/Comment';
import { ICONS } from '@Constants/iconsApp';
import { ListActionType } from '@Types';

import './Comments.scss';

export interface MessageProps {
    objectId: string;
    user: string;
    date: number;
    message: string;
    avatar?: string;
    onEdit?: (text: string, id: string) => void;
    onDelete?: (id: string) => void;
    readOnly?: boolean;
    isMine?: boolean;
    edited?: number;
    prefix?: ReactNode;
}

const Message = ({
    objectId,
    user,
    avatar = '',
    date,
    message,
    onEdit,
    onDelete,
    readOnly = true,
    isMine = false,
    edited,
    prefix,
}: MessageProps) => {
    const i = useLocalMessage();

    const [editMode, setEditMode] = useState(false);
    const [content, setContent] = useState(message);

    const onSubmitHandler = (txt: string) => {
        onEdit?.(txt, objectId);
        setContent(txt);
        setEditMode(false);
    };

    const onToggle = () => {
        setEditMode((prev) => !prev);
    };

    const onDeleteHandle = () => {
        onDelete?.(objectId);
    };

    const actions: { [key: string]: ListActionType } = {
        edit: {
            hidden: !isMine && readOnly,
            label: i('comment.actions.edit'),
            icon: <ICONS.EditIcon />,
            query: onToggle,
        },
        delete: {
            hidden: !isMine && readOnly,
            label: i('comment.actions.delete'),
            icon: <ICONS.DeleteIcon />,
            confirm: true,
            message: i('comment.actions.approveCancel'),
            query: onDeleteHandle,
        },
    };

    return (
        <Comment
            date={date}
            actions={isMine && !readOnly ? actions : {}}
            avatar={avatar}
            content={
                editMode && !readOnly && isMine ? (
                    <CommentForm onSubmit={onSubmitHandler} onReset={onToggle} message={content} isInComment />
                ) : (
                    <p className="Comment__Text">
                        {prefix || null}
                        {content}
                    </p>
                )
            }
            edited={edited}
            editedPrefix={i('comment.updated')}
            name={user}
            isChatComment
            isCurrentUser={isMine}
            isEditing={editMode && !readOnly && isMine}
        />
    );
};

interface CommentsProps {
    data: MessageProps[];
    showMore?: boolean;
    onEdit?: (text: string, id: string) => void;
    onDelete?: (id: string) => void;
    moreConfig?: {
        message?: string;
        onClick?: () => void;
        show?: boolean;
    };
}

export const Comments = ({ data, onEdit, onDelete, moreConfig }: CommentsProps): ReactElement => {
    const onClickHandler = useCallback(() => {
        moreConfig?.onClick?.();
    }, [moreConfig]);

    return (
        <>
            {moreConfig?.onClick && moreConfig?.show ? (
                <div
                    className="comment-more"
                    onClick={onClickHandler}
                    style={{ ['--comment-msg-more' as any]: `"${moreConfig?.message}"` }}
                />
            ) : null}
            <div className="comment">
                <div className="comment-list Comment__List">
                    {data.map((info: any) => (
                        <Message key={info.objectId} {...info} onEdit={onEdit} onDelete={onDelete} />
                    ))}
                </div>
            </div>
        </>
    );
};
